import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { getSurplus, saveSurplus, checkSurplusSaved, createSurplusInquiry, createConversation } from '@/lib/api';
import MobileBottomNav from '@/components/layout/MobileBottomNav';
import TexHubLogo from '@/components/brand/TexHubLogo';
import { 
  ArrowLeft,
  MapPin,
  Building2,
  Clock,
  Package,
  Eye,
  MessageSquare,
  Heart,
  Share2,
  FileText,
  CheckCircle2,
  Zap,
  Send,
  ExternalLink,
  Loader2,
  ImageIcon,
  ChevronLeft,
  ChevronRight,
  Scale,
  Ruler,
  Palette,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const SurplusDetail = () => {
  const { surplusId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { isUSA } = useLocale();
  const [surplus, setSurplus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [savingToFavorites, setSavingToFavorites] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    fetchSurplus();
    if (isAuthenticated) {
      checkIfSaved();
    }
  }, [surplusId, isAuthenticated]);

  const fetchSurplus = async () => {
    try {
      const data = await getSurplus(surplusId);
      setSurplus(data);
    } catch (error) {
      console.error('Error fetching surplus:', error);
      toast.error(isUSA ? 'Error loading surplus' : 'Error al cargar el excedente');
    } finally {
      setLoading(false);
    }
  };

  const checkIfSaved = async () => {
    try {
      const result = await checkSurplusSaved(surplusId);
      setIsSaved(result.saved);
    } catch (error) {
      console.error('Error checking saved status:', error);
    }
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    setSavingToFavorites(true);
    try {
      const result = await saveSurplus(surplusId);
      setIsSaved(result.saved);
      toast.success(result.message);
    } catch (error) {
      toast.error(isUSA ? 'Error saving' : 'Error al guardar');
    } finally {
      setSavingToFavorites(false);
    }
  };

  const handleContact = async (type) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Register inquiry
    try {
      await createSurplusInquiry(surplusId);
    } catch (error) {
      console.error('Error registering inquiry:', error);
    }

    if (type === 'message') {
      setShowContactDialog(true);
    } else if (type === 'quote') {
      navigate(`/dashboard/quotes?product=${surplus.name}&company=${surplus.company_id}`);
    } else if (type === 'sample') {
      navigate(`/dashboard/samples/new?surplus=${surplusId}`);
    }
  };

  const sendMessage = async () => {
    if (!contactMessage.trim()) {
      toast.error(isUSA ? 'Write a message' : 'Escribe un mensaje');
      return;
    }

    setSendingMessage(true);
    try {
      await createConversation({
        recipient_company_id: surplus.company_id,
        subject: isUSA ? `Inquiry about: ${surplus.name}` : `Consulta sobre: ${surplus.name}`,
        initial_message: contactMessage
      });
      toast.success(isUSA ? 'Message sent successfully' : 'Mensaje enviado correctamente');
      setShowContactDialog(false);
      setContactMessage('');
    } catch (error) {
      toast.error(isUSA ? 'Error sending message' : 'Error al enviar mensaje');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(isUSA ? 'en-US' : 'es-MX', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      disponible: { bg: '#34B772', text: isUSA ? 'Available' : 'Disponible' },
      reservado: { bg: '#F59E0B', text: isUSA ? 'Reserved' : 'Reservado' },
      vendido: { bg: '#6B7280', text: isUSA ? 'Sold' : 'Vendido' },
      expirado: { bg: '#EF4444', text: isUSA ? 'Expired' : 'Expirado' }
    };
    const style = styles[status] || styles.disponible;
    return (
      <Badge style={{ backgroundColor: style.bg }} className="text-white">
        {style.text}
      </Badge>
    );
  };

  const getTypeBadge = (type, label) => {
    const colors = {
      tela_saldo: '#1E4C8E',
      inventario_disponible: '#059669',
      produccion_cancelada: '#DC2626',
      excedente_fabricacion: '#7C3AED',
      capacidad_maquila: '#0891B2',
      lote_produccion: '#EA580C',
      remate_liquidacion: '#DB2777'
    };
    return (
      <Badge 
        variant="outline" 
        style={{ borderColor: colors[type] || '#6B7280', color: colors[type] || '#6B7280' }}
      >
        {label}
      </Badge>
    );
  };

  const nextImage = () => {
    if (surplus?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % surplus.images.length);
    }
  };

  const prevImage = () => {
    if (surplus?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + surplus.images.length) % surplus.images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!surplus) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h2 className="text-lg font-semibold text-slate-700 mb-2">{isUSA ? 'Surplus not found' : 'Excedente no encontrado'}</h2>
          <Link to="/surplus">
            <Button variant="outline">{isUSA ? 'View all surplus' : 'Ver todos los excedentes'}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isOwner = user?.company_id === surplus.company_id;

  return (
    <div className="min-h-screen bg-slate-50 pb-20 lg:pb-0">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="container-app">
          <div className="flex items-center justify-between h-14">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">{isUSA ? 'Back' : 'Volver'}</span>
            </button>
            <Link to="/">
              <TexHubLogo size="small" />
            </Link>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={handleSave} disabled={savingToFavorites}>
                <Heart className={`w-5 h-5 ${isSaved ? 'fill-red-500 text-red-500' : ''}`} />
              </Button>
              <Button variant="ghost" size="icon">
                <Share2 className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container-app py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <Card className="overflow-hidden">
              <div className="relative aspect-video bg-slate-100">
                {surplus.images && surplus.images.length > 0 ? (
                  <>
                    <img 
                      src={surplus.images[currentImageIndex]} 
                      alt={surplus.name}
                      className="w-full h-full object-contain"
                    />
                    {surplus.images.length > 1 && (
                      <>
                        <button 
                          onClick={prevImage}
                          className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center hover:bg-white"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button 
                          onClick={nextImage}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center hover:bg-white"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                          {surplus.images.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={() => setCurrentImageIndex(idx)}
                              className={`w-2 h-2 rounded-full transition-colors ${
                                idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-20 h-20 text-slate-300" />
                  </div>
                )}
              </div>
              {surplus.images && surplus.images.length > 1 && (
                <div className="p-3 flex gap-2 overflow-x-auto">
                  {surplus.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 ${
                        idx === currentImageIndex ? 'border-[#1E4C8E]' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </Card>

            {/* Details */}
            <Card>
              <CardContent className="p-6">
                {/* Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {getTypeBadge(surplus.surplus_type, surplus.surplus_type_label)}
                  {getStatusBadge(surplus.status)}
                  {surplus.immediate_availability && (
                    <Badge className="bg-yellow-400 text-yellow-900">
                      <Zap className="w-3 h-3 mr-1" />
                      {isUSA ? 'Available now' : 'Disponible ya'}
                    </Badge>
                  )}
                </div>

                {/* Title */}
                <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {surplus.name}
                </h1>

                {/* Meta */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {surplus.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {surplus.views_count || 0} {isUSA ? 'views' : 'vistas'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDate(surplus.created_at)}
                  </span>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <h3 className="font-semibold text-slate-900 mb-2">{isUSA ? 'Description' : 'Descripción'}</h3>
                  <p className="text-slate-600 whitespace-pre-wrap">{surplus.description}</p>
                </div>

                {/* Specifications */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">{isUSA ? 'Technical specifications' : 'Especificaciones técnicas'}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {surplus.composition && (
                      <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                        <Package className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">{isUSA ? 'Composition' : 'Composición'}</p>
                          <p className="font-medium text-slate-900">{surplus.composition}</p>
                        </div>
                      </div>
                    )}
                    {surplus.weight_gsm && (
                      <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                        <Scale className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">{isUSA ? 'Weight' : 'Gramaje'}</p>
                          <p className="font-medium text-slate-900">{surplus.weight_gsm} g/m²</p>
                        </div>
                      </div>
                    )}
                    {surplus.width_cm && (
                      <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                        <Ruler className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">{isUSA ? 'Width' : 'Ancho'}</p>
                          <p className="font-medium text-slate-900">{surplus.width_cm} cm</p>
                        </div>
                      </div>
                    )}
                    {surplus.color && (
                      <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                        <Palette className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">{isUSA ? 'Color' : 'Color'}</p>
                          <p className="font-medium text-slate-900">{surplus.color}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      <Package className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">{isUSA ? 'Quantity' : 'Cantidad'}</p>
                        <p className="font-medium text-slate-900">{surplus.quantity} {surplus.unit}</p>
                      </div>
                    </div>
                    {surplus.expiration_date && (
                      <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                        <Calendar className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">{isUSA ? 'Available until' : 'Disponible hasta'}</p>
                          <p className="font-medium text-slate-900">{formatDate(surplus.expiration_date)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Price Card */}
            <Card>
              <CardContent className="p-6">
                {surplus.price && !surplus.hide_price ? (
                  <>
                    <p className="text-sm text-slate-500 mb-1">{isUSA ? 'Price' : 'Precio'}</p>
                    <p className="text-3xl font-bold text-[#1E4C8E] mb-4">
                      ${surplus.price.toLocaleString(isUSA ? 'en-US' : 'es-MX')} <span className="text-lg font-normal">{surplus.currency || 'MXN'}</span>
                    </p>
                  </>
                ) : (
                  <p className="text-lg text-slate-600 mb-4">{isUSA ? 'Price on request' : 'Precio a cotizar'}</p>
                )}

                <p className="text-sm text-slate-600 mb-6">
                  <span className="font-semibold">{surplus.quantity}</span> {surplus.unit} {isUSA ? 'available' : 'disponibles'}
                </p>

                {!isOwner && (
                  <div className="space-y-3">
                    <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
                      <DialogTrigger asChild>
                        <Button 
                          className="w-full hover:opacity-90" 
                          style={{ backgroundColor: '#34B772' }}
                          onClick={() => handleContact('message')}
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          {isUSA ? 'Send message' : 'Enviar mensaje'}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{isUSA ? 'Contact seller' : 'Contactar al vendedor'}</DialogTitle>
                          <DialogDescription>
                            {isUSA ? `Send a message about: ${surplus.name}` : `Envía un mensaje sobre: ${surplus.name}`}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <Textarea
                            placeholder={isUSA 
                              ? `Hello, I'm interested in the ${surplus.surplus_type_label?.toLowerCase() || 'surplus'}: ${surplus.name}. Could you provide more information?`
                              : `Hola, me interesa el ${surplus.surplus_type_label?.toLowerCase() || 'excedente'}: ${surplus.name}. ¿Podrían darme más información?`}
                            rows={4}
                            value={contactMessage}
                            onChange={(e) => setContactMessage(e.target.value)}
                          />
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setShowContactDialog(false)}>
                              {isUSA ? 'Cancel' : 'Cancelar'}
                            </Button>
                            <Button 
                              style={{ backgroundColor: '#34B772' }}
                              className="hover:opacity-90"
                              onClick={sendMessage}
                              disabled={sendingMessage}
                            >
                              {sendingMessage ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Send className="w-4 h-4 mr-2" />
                                  {isUSA ? 'Send' : 'Enviar'}
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => handleContact('quote')}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      {isUSA ? 'Request quote' : 'Solicitar cotización'}
                    </Button>

                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => handleContact('sample')}
                    >
                      <Package className="w-4 h-4 mr-2" />
                      {isUSA ? 'Request sample' : 'Solicitar muestra'}
                    </Button>
                  </div>
                )}

                {isOwner && (
                  <Link to={`/dashboard/surplus/${surplus.surplus_id}/edit`}>
                    <Button variant="outline" className="w-full">
                      {isUSA ? 'Edit surplus' : 'Editar excedente'}
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>

            {/* Company Card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1E4C8E' }}>
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{surplus.company_name}</h3>
                      {surplus.company_verified && (
                        <CheckCircle2 className="w-4 h-4 text-[#34B772]" />
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{isUSA ? 'Supplier' : 'Proveedor'}</p>
                  </div>
                </div>

                <Link to={`/company/${surplus.company_id}`}>
                  <Button variant="outline" className="w-full">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {isUSA ? 'View profile' : 'Ver perfil'}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <MobileBottomNav />
    </div>
  );
};

export default SurplusDetail;
